import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Mail, Building2, Loader2, FileDown } from "lucide-react";

const STATUSES = ["New", "Contacted", "Scheduled", "Closed"];
const TONE = {
  New: "bg-teal-100 text-teal-900",
  Contacted: "bg-amber-100 text-amber-900",
  Scheduled: "bg-sky-100 text-sky-900",
  Closed: "bg-slate-200 text-slate-700",
};

export default function DemoRequests() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async (status) => {
    setLoading(true);
    try {
      const { data } = await api.get("/demo-requests",
                                     { params: status && status !== "all" ? { status } : {} });
      setRows(data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  useEffect(() => { load(filter); }, [filter]);

  const save = async (id, body) => {
    try {
      const { data } = await api.patch(`/demo-requests/${id}`, body);
      setRows((rs) => rs.map((r) => (r.id === id ? data : r)));
      toast.success("Demo request updated");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="demo-requests-page">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Demo requests</h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Enquiries submitted from the public PathQity landing page.
          </p>
        </div>
        <div className="ml-auto w-44">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="demo-requests-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading && <Loader2 className="animate-spin text-slate-400" size={20} />}

      {!loading && rows.length === 0 && (
        <p className="border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500"
           data-testid="demo-requests-empty">
          No demo requests yet.
        </p>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {rows.map((r) => (
          <article key={r.id} className="border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700"
                   data-testid={`demo-request-${r.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-display text-lg font-bold">{r.name}</h2>
              <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${TONE[r.status] || TONE.New}`}>
                {r.status}
              </span>
              {r.source === "proposal-download" && (
                <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-900"
                      data-testid={`demo-source-proposal-${r.id}`}>
                  <FileDown size={11} /> Proposal download
                </span>
              )}
              <span className="ml-auto text-xs text-slate-500">
                {new Date(r.created_at).toLocaleString()}
              </span>
            </div>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              {r.title || "Title not given"}
            </p>
            <p className="mt-2 flex items-center gap-2 text-sm">
              <Mail size={14} className="text-[#134e4a]" />
              <a href={`mailto:${r.email}`} className="font-semibold text-[#134e4a] hover:underline">
                {r.email}
              </a>
            </p>
            {r.institution && (
              <p className="mt-1 flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                <Building2 size={14} className="text-slate-400" /> {r.institution}
              </p>
            )}
            {r.message && (
              <p className="mt-3 border-l-2 border-slate-200 pl-3 text-sm italic text-slate-600 dark:text-slate-300">
                {r.message}
              </p>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-2">
              {STATUSES.map((s) => (
                <button key={s} onClick={() => save(r.id, { status: s })}
                        data-testid={`demo-status-${s.toLowerCase()}-${r.id}`}
                        className={`rounded border px-2.5 py-1 text-xs font-semibold transition-colors ${
                          r.status === s ? "border-[#134e4a] bg-[#134e4a] text-white"
                            : "border-slate-300 text-slate-600 hover:border-[#134e4a] hover:text-[#134e4a]"}`}>
                  {s}
                </button>
              ))}
            </div>

            <Textarea className="mt-3" rows={2} defaultValue={r.notes}
                      data-testid={`demo-notes-${r.id}`}
                      placeholder="Follow-up notes…"
                      onBlur={(e) => {
                        if (e.target.value.trim() !== (r.notes || "")) {
                          save(r.id, { notes: e.target.value });
                        }
                      }} />
          </article>
        ))}
      </div>
    </div>
  );
}
